import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Sparkles, CheckCircle2, XCircle, Clock, Share2, MessageCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function statusMeta(s) {
  switch (s) {
    case "paid": return { Icon: CheckCircle2, label: "Premiado / Pagado", cls: "bg-green-600 text-white" };
    case "cancelled": return { Icon: XCircle, label: "Cancelado", cls: "bg-red-600 text-white" };
    case "won": return { Icon: CheckCircle2, label: "Ganador", cls: "bg-emerald-600 text-white" };
    case "lost": return { Icon: XCircle, label: "No premiado", cls: "bg-slate-600 text-white" };
    case "active":
    default: return { Icon: Clock, label: "Vigente · Espera sorteo", cls: "bg-blue-600 text-white" };
  }
}

export default function PublicTicket() {
  const { ticketNumber } = useParams();
  const [searchParams] = useSearchParams();
  const [ticket, setTicket] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const v = searchParams.get("v");
    axios
      .get(`${BACKEND_URL}/api/public/tickets/${encodeURIComponent(ticketNumber)}`, {
        params: v ? { v } : undefined,
      })
      .then(({ data }) => setTicket(data))
      .catch(() => setErr("Ticket no encontrado o inválido."));
  }, [ticketNumber, searchParams]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = ticket
    ? `Ticket ${ticket.ticket_number} · Banca POS · RD$${(ticket.total_amount || 0).toFixed(2)}\n${shareUrl}`
    : shareUrl;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Enlace copiado");
    } catch { toast.error("No se pudo copiar"); }
  };

  const openWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener");
  };

  const openSMS = () => {
    // iOS: sms:?body= · Android: sms:?body= too (encoded)
    window.location.href = `sms:?body=${encodeURIComponent(shareText)}`;
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Ticket ${ticket.ticket_number}`, text: shareText, url: shareUrl });
      } catch (e) {
        // User cancelled or share failed — silent, but surface in dev tools
        console.debug("navigator.share aborted", e);
      }
    } else {
      copyLink();
    }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-6">
        <div className="max-w-sm w-full text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: "Manrope" }}>Ticket no encontrado</h1>
          <p className="text-sm text-slate-400 mt-2">Verifica el número del ticket con la banca donde compraste tu jugada.</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center text-sm">
        Cargando…
      </div>
    );
  }

  const d = new Date(ticket.created_at);
  const meta = statusMeta(ticket.status);
  const StatusIcon = meta.Icon;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Toaster position="top-center" richColors />
      <div className="max-w-md mx-auto px-4 py-6" data-testid="public-ticket-page">
        {/* Brand */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 bg-blue-600 rounded-sm flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-black text-lg tracking-tight" style={{ fontFamily: "Manrope" }}>BANCA POS</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
              Copia digital · Rep. Dominicana
            </div>
          </div>
        </div>

        {/* Ticket card */}
        <div className="bg-white text-slate-950 rounded-sm p-5">
          {/* Status */}
          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-black uppercase tracking-widest ${meta.cls}`} data-testid="public-ticket-status">
            <StatusIcon className="w-3 h-3" /> {meta.label}
          </div>

          {/* Ticket number */}
          <div className="mt-3 font-mono text-sm text-slate-500">Número de ticket</div>
          <div className="font-black font-mono text-xl tracking-wide mt-1" data-testid="public-ticket-number">
            {ticket.ticket_number}
          </div>

          {/* Meta */}
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Sucursal</div>
              <div className="font-mono font-bold text-slate-900">{ticket.branch_code}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Estado</div>
              <div className="font-mono font-bold text-slate-900 truncate uppercase">{ticket.status}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Fecha</div>
              <div className="font-mono text-slate-900">{d.toLocaleDateString("es-DO")}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Hora</div>
              <div className="font-mono text-slate-900">{d.toLocaleTimeString("es-DO")}</div>
            </div>
          </div>

          <div className="border-t border-dashed border-slate-300 my-4" />

          {/* Items */}
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Jugadas</div>
          <ul className="space-y-2" data-testid="public-ticket-items">
            {ticket.items.map((it) => (
              <li key={it.id} className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] text-slate-500 truncate">{it.game_name} · {it.play_type_name}</div>
                  <div className="font-mono font-black text-slate-950 text-base tracking-wider">{it.display}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-bold text-slate-900">RD${(it.amount || 0).toFixed(2)}</div>
                  <div className="text-[10px] font-mono text-green-700">→ RD${(it.potential_prize || 0).toFixed(2)}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="border-t border-dashed border-slate-300 my-4" />

          {/* Total */}
          <div className="flex items-baseline justify-between">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Total pagado</div>
            <div className="font-black font-mono text-2xl text-slate-950" data-testid="public-ticket-total">
              RD${(ticket.total_amount || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>

          {ticket.total_prize > 0 && (
            <div className="mt-2 flex items-baseline justify-between bg-green-50 border border-green-200 rounded-sm px-3 py-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-green-700">Premio</div>
              <div className="font-black font-mono text-xl text-green-700">
                RD${(ticket.total_prize || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          )}

          {/* QR */}
          <div className="mt-5 flex flex-col items-center">
            <div className="bg-white p-2 border border-slate-200 rounded-sm">
              <QRCodeSVG value={ticket.ticket_number} size={160} level="M" includeMargin={false} />
            </div>
            <div className="mt-2 text-[10px] font-mono text-slate-500 uppercase tracking-widest">
              Escanea en la banca para cobrar
            </div>
          </div>
        </div>

        {/* Share buttons */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <button
            onClick={openWhatsApp}
            className="h-12 rounded-sm bg-[#25d366] text-white font-bold text-xs flex items-center justify-center gap-1.5"
            data-testid="share-whatsapp"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </button>
          <button
            onClick={openSMS}
            className="h-12 rounded-sm bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-1.5"
            data-testid="share-sms"
          >
            <Share2 className="w-4 h-4" /> SMS
          </button>
          <button
            onClick={copyLink}
            className="h-12 rounded-sm bg-white text-slate-950 font-bold text-xs flex items-center justify-center gap-1.5 border border-slate-300"
            data-testid="share-copy"
          >
            <Copy className="w-4 h-4" /> Copiar
          </button>
        </div>

        {typeof navigator !== "undefined" && navigator.share && (
          <button
            onClick={nativeShare}
            className="w-full mt-2 h-11 rounded-sm border border-white/30 text-white font-semibold text-xs"
            data-testid="share-native"
          >
            Compartir con otra app
          </button>
        )}

        <div className="mt-6 text-center text-[10px] font-mono text-slate-500 uppercase tracking-widest">
          Este es un ticket digital · No sustituye el papel para cobros presenciales
        </div>
      </div>
    </div>
  );
}
